import { Divider } from '@material-ui/core'
import {
  getValueFromLocalStorage,
  setValueInLocalStorage
} from '@openhouse-technologies/utils'
import { isNil, isEmpty, get } from 'lodash'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useCart } from 'react-use-cart'

import { fetchUser, getCategoryCost } from 'api'
import ModalForm from 'app/components/ModalForm'
import { navigationPaths } from 'app/config'
import constants from 'app/constants'
import { confirm, eventAlert } from 'app/helpers'
import '../../styles.css'
import {
  batchTypesMap,
  conflictTypes
} from 'app/pages/Class/Inventory/constants'
import { composeBatchDetails } from 'app/pages/Class/Inventory/transformers'

import { Actions } from './components/Actions'
// import { CoinSummary } from './components/CoinSummary'
import { CourseDetail } from './components/CourseDetail'
import { ParentChildForm } from './components/ParentChildForm'
import { demoSchema } from './formConfig'
import * as Styles from './styles'

import { SeparationContainer } from '../../../styles'
import {
  calculateEndDate,
  formatEndDate,
  getCartItemStructure,
  validateBatchInfoDate,
  validateCourseDetail,
  validateItemForCart,
  validateParentDetails,
  excelFlowValidateStudentBatchDetails
} from '../helpers'

const AddToCartModal = ({
  isModalOpen,
  bookingType,
  selectedItem,
  onHandleModalClose,
  selectedBatchType,
  isEditing,
  editingItem
}) => {
  const LinkRedirector = ({ href }) => {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ cursor: 'pointer' }}>
        here
      </a>
    )
  }

  const {
    addItem: addItemToCart,
    isEmpty: isCartEmpty,
    items: currentCartItems,
    updateItem: updateCartItem,
    setCartMetadata,
    metadata: cartMetadata,
    emptyCart
  } = useCart()

  const [formData, setFormData] = useState({})
  const [batchInfo, setBatchInfo] = useState({})
  const [errorSchema, setErrorSchema] = useState({})

  const isParentLocked =
    !isCartEmpty && [batchTypesMap.excel].includes(cartMetadata?.cartType)

  const [selectedParentDetails, setSelectedParentDetails] = useState(
    isParentLocked ? getValueFromLocalStorage('CART_PARENT') : {} || {}
  )

  const [selectedStudentDetails, setSelectedStudentDetails] = useState({})
  const [students, setStudents] = useState([])
  const [invalidCoins, setIsInvalidCoins] = useState(false)
  const [coinData, setCoinData] = useState({})
  const [inputError, setInputError] = useState({
    parent: null,
    student: null,
    isDiscover: null
  })
  const [commonCourses, setCommonCourses] = React.useState([])
  const [eligibleGrades, setEligibleGrades] = React.useState([])
  const [disableAddToCart, setDisableAddtoCart] = React.useState(true)
  const [courseFormDisabled, setCourseFormDisabled] = React.useState(true)
  const [courseFormValid, setCourseFormValid] = React.useState(false)
  const [studentConflictDetails, setStudentConflictDetails] = React.useState({
    isStudentValid: null,
    message: '',
    description: '',
    makeup_enrolments: []
  })

  const [enableDiscovery, setEnableDiscovery] = React.useState(null)
  const [discoveryEndDate, setDiscoveryEndDate] = React.useState(null)
  const [resubscribeStartDate, setResubscribeStartDate] = React.useState(null)

  const [currentBatchInfo, setCurrentBatchInfo] = React.useState({})

  const formRef = useRef(null)
  const dispatch = useDispatch()

  useEffect(() => {
    if (isEditing) {
      if (!editingItem) return
    } else if (!selectedItem) return
    handleSelectedParentChange()
  }, [selectedItem, editingItem, isEditing])

  // The date the operator has picked for THIS batch, or null.
  //
  // The Excel picker stores {label: '<YYYY-MM-DD>', value: '<schedule uuid>'} --
  // the DATE is the LABEL. (The program flow stores a date in `value`, which is
  // why its fix reads differently.) Sending `.value` here posts a UUID, the
  // endpoint rejects it with "start_date must be YYYY-MM-DD", and the form ends
  // up locked with nothing shown to the operator.
  const selectedStartDate =
    get(batchInfo, [selectedItem?.uuid, 'startDate', 'label']) || null

  useEffect(() => {
    ;(async () => {
      try {
      if (!isEmpty(selectedParentDetails) && !isEmpty(selectedStudentDetails)) {
        const isParentValid = validateParentDetails(
          selectedParentDetails
        )?.isValid
        const {
          isValid: isStudentValid,
          invalidAttributes: studentInvalidAttributes,
          message,
          description,
          makeup_enrolments,
          enable_discovery,
          discovery_end_date,
          resubscribe_start_date
        } = await excelFlowValidateStudentBatchDetails(
          selectedStudentDetails,
          selectedItem,
          selectedStartDate
        )

        setStudentConflictDetails({
          isStudentValid,
          message,
          description,
          makeup_enrolments
        })

        setDiscoveryEndDate(discovery_end_date)
        setResubscribeStartDate(resubscribe_start_date)

        setEnableDiscovery(enable_discovery)
        // `enable_discovery` answers "COULD this student have a discovery in
        // this subject". `isDiscover` answers "IS this booking a discovery".
        // They are different questions and this seeded the second from the
        // first, so every eligible student defaulted to Discovery.
        //
        // Worse, this effect re-runs on `selectedStartDate` ("picking a date has
        // to RE-ASK the backend"), and it overwrote isDiscover each time. An
        // operator who answered No and THEN picked a start date had their answer
        // silently reset to Discovery. 159 Alfred bookings carried the flag
        // between 1 Sep and 8 Oct 2026, every one of them charged full price.
        //
        // Eligible => leave the operator's answer alone, and start UNSET so they
        // have to give one; the form already refuses to submit while it is nil
        // (validateCourseDetail's `isValidIsDiscover = !isNil(isDiscover)`).
        //
        // NOT eligible => false, because the control is disabled
        // (`discoveryLocked`) and an unset value on a disabled control is
        // unanswerable: it would block the booking outright.
        setBatchInfo((state) => ({
          ...state,
          [selectedItem?.uuid]: {
            ...state[selectedItem?.uuid],
            isDiscover:
              enable_discovery === false
                ? false
                : state[selectedItem?.uuid]?.isDiscover
          }
        }))

        if (!isStudentValid && studentInvalidAttributes?.length > 0) {
          setInputError((prev) => ({
            ...prev,
            student: (
              <div>
                Student details ({studentInvalidAttributes.join(', ')}) invalid,
                click{' '}
                <LinkRedirector
                  href={`${window.location.origin}${navigationPaths.student}?uuid=${selectedStudentDetails?.user?.uuid}&editMode=1`}
                />{' '}
                to update
              </div>
            )
          }))
        } else if (!isStudentValid && message) {
          setInputError((prev) => ({
            ...prev,
            student: message
          }))
        }

        const parentStudentValid = isParentValid && isStudentValid
        setCourseFormDisabled(!parentStudentValid)
      } else {
        setCurrentBatchInfo({})
        setCourseFormDisabled(true)
      }
      } catch (error) {
        // Previously an unhandled rejection: the form simply stayed locked and
        // the operator was told nothing. Surface it instead.
        console.log('error checking batch eligibility :- ', error)
        setCourseFormDisabled(true)
        setInputError((prev) => ({
          ...prev,
          student: 'Could not check this batch. Please retry.'
        }))
      }
    })()
    // `selectedStartDate` is in here on purpose: picking a date has to RE-ASK
    // the backend, or the verdict stays the one computed with no date at all.
  }, [
    selectedParentDetails,
    selectedStudentDetails,
    selectedItem,
    selectedStartDate
  ])

  // A class conflict must not lock the START DATE field, because the conflict is
  // judged AS OF that date -- locking it makes the verdict unchallengeable and is
  // what blocked every "active this month, booking next month" parent.
  //
  // Everything else still locks it, and Add-to-Cart stays gated on
  // `courseFormDisabled` either way: this front gate is the ONLY cross-batch
  // time-conflict check for Excel (the cart's own check is behind
  // `is_play_care`, and checkout only looks at the same batch), so it is
  // deferred here, never removed.
  const isClassConflictOnly =
    studentConflictDetails?.message === conflictTypes.CLASS_CONFLICT

  useEffect(() => {
    setDisableAddtoCart(courseFormDisabled || !courseFormValid)
  }, [courseFormDisabled, courseFormValid])

  const handleSelectedParentChange = () => {
    if (!isEmpty(selectedParentDetails)) {
      setSelectedStudentDetails({})
      setCommonCourses({})
      const grades = []

      if (selectedParentDetails.students.length === 0) {
        eventAlert(
          'No students found! Add a student for the parent.',
          constants.ERROR
        )
      }

      const mappedStudents = selectedParentDetails?.students.map((student) => {
        const isEligible = eligibleGrades.includes(student?.standard?.name)
        return {
          ...student,
          isDisabled: !isEligible,
          message: isEligible ? null : ''
        }
      })

      const eligibleStudents = mappedStudents.filter(
        (student) => !student.isDisabled
      )

      if (
        eligibleStudents.length === 0 &&
        selectedParentDetails?.students?.length > 0
      ) {
        // eventAlert(
        //   'No students match the grade for the selected course!',
        //   constants.ERROR
        // )
      }

      setStudents(mappedStudents || [])

      handleValidateParentDetails(selectedParentDetails)
      if (isEditing) setSelectedStudentDetails(editingItem.userInfo.student)
    } else {
      setStudents([])
      setSelectedStudentDetails({})
      setCommonCourses({})
      setInputError({
        parent: null,
        student: null,
        isDiscover: null
      })
      setDisableAddtoCart(true)
      setCurrentBatchInfo({})
    }
  }

  useEffect(() => {
    handleSelectedParentChange()
  }, [selectedParentDetails])

  useEffect(() => {
    if (isModalOpen) {
      if (isEditing && editingItem) {
        setSelectedParentDetails(editingItem?.userInfo?.parent)
        setBatchInfo({
          [editingItem?.batchDetails?.uuid]: editingItem?.batchFormInfo
        })
      } else if (selectedItem) {
        const { uuid, standards } = composeBatchDetails(selectedItem)
        const grades = standards?.map((std) => std.name) || []
        setBatchInfo((state) => {
          const batchId = uuid

          const { isDiscover, startDate, endDate } = state[batchId] || {}

          let newState = {
            ...state,
            [batchId]: {
              ...state[batchId],
              price: null,
              isDiscover: isNil(isDiscover) ? '' : isDiscover,
              startDate: isNil(startDate) ? null : startDate,
              endDate: isNil(endDate) ? null : endDate
            }
          }

          return newState
        })

        setEligibleGrades(grades)
      }
    }
  }, [isModalOpen, selectedItem, isEditing, editingItem])

  const handleParentSelection = async (name, value) => {
    setSelectedParentDetails(value || {})
    if (!isNil(value)) {
      await verifyDetails(value?.uuid)
    }
  }

  const handleStudentSelection = (event) => {
    const { value } = event.target
    const selectedStudent = students.find((item) => item.user.uuid === value)
    let studentDetails = selectedStudent || {}
    if (selectedStudent) {
      studentDetails.parent = selectedParentDetails.uuid
    }
    setSelectedStudentDetails(studentDetails)
  }

  const ReVerifyButton = () => {
    return (
      <>
        if updated, click{' '}
        <span
          onClick={verifyDetails}
          style={{
            textDecoration: 'underline',
            color: 'blue',
            cursor: 'pointer'
          }}>
          here
        </span>{' '}
        to verify
      </>
    )
  }

  const verifyDetails = async (parent_uuid) => {
    try {
      const queries = { uuid: parent_uuid }
      const response = await fetchUser({ queries }, 'parent')

      if (response.results.length === 1) {
        const item = response.results[0]
        setSelectedParentDetails({
          uuid: item.user.uuid,
          value: item.user.uuid,
          name: `${item.user.phone_number} (${item.user.first_name} ${item.user.last_name})`,
          first_name: item.user.first_name,
          last_name: item.user.last_name,
          phone_number: item.user.phone_number,
          state: item.user.state,
          email: item.user.email_address,
          students: item.students,
          coins: item.user.coin_summary?.remaining_coins,
          credits: item.user.credits,
          member_status: item.user.member_status,
          centre_preference: item.centre_preference
        })

        setStudents(item.students || [])
      } else {
        throw new Error('Multiple users found with the same number')
      }
    } catch (error) {
      console.error('Error in verifying user details:', error)
      eventAlert(error.message, constants.ERROR)
    }
  }

  const handleValidateParentDetails = (selectedParentDetails) => {
    let error = null,
      isValid = false

    if (isEmpty(selectedParentDetails)) {
      error = 'Please Select Parent'
    } else {
      const result = validateParentDetails(selectedParentDetails)

      isValid = result.isValid

      if (!isValid) {
        error = (
          <div>
            Parent details ({result.invalidAttributes.join(', ')}) invalid,
            click{' '}
            <LinkRedirector
              href={`${window.location.origin}${navigationPaths.parent}?uuid=${selectedParentDetails?.uuid}&editMode=1`}
            />{' '}
            to update, <ReVerifyButton />
          </div>
        )
      }
    }

    setInputError((state) => ({
      ...state,
      parent: error
    }))

    return isValid
  }

  useEffect(() => {
    const { batch } = currentBatchInfo

    if (
      currentBatchInfo.startDate &&
      (currentBatchInfo.isDiscover === true ||
        currentBatchInfo.isDiscover === false)
    ) {
      ;(async () => {
        const categoryCost = await getCategoryCost(currentBatchInfo)

        if (categoryCost?.success) {
          const {
            data: { end_date, cost }
          } = categoryCost
          setBatchInfo((state) => {
            const newBatchInfo = {
              ...state,
              [batch]: {
                ...state[batch],
                endDate: {
                  value: end_date,
                  label: formatEndDate(end_date)
                },
                price: cost
              }
            }
            setCourseFormValid(
              validateCourseDetail(
                newBatchInfo[batch],
                selectedItem?.course?.subjects?.[0]
              )
            )

            return newBatchInfo
          })
        }
      })()
    }
  }, [currentBatchInfo, selectedItem])
  const handleBatchInfoChange = async (batchId, property, value) => {
    setBatchInfo((prevState) => {
      const newBatchInfo = {
        ...prevState,
        [batchId]: {
          ...prevState[batchId],
          [property]: value
        }
      }
      if (property === 'startDate') {
        setCurrentBatchInfo({
          batch: selectedItem?.uuid,
          startDate: value,
          isDiscover: newBatchInfo[batchId]?.isDiscover
        })
      } else if (property === 'isDiscover') {
        setCurrentBatchInfo({
          batch: selectedItem.uuid,
          startDate: newBatchInfo[batchId].startDate,
          isDiscover: value
        })
      }
      return newBatchInfo
    })
  }

  const onSubmitForm = ({ formData }) => {
    setFormData(formData)
  }

  const handleOnFormDataChange = ({ formData }) => {
    if (formRef.current) {
      const { errorSchema } = formRef.current.validate(formData)
      const modifiedErrorSchema = {}

      Object.keys(errorSchema).forEach((key) => {
        if (formData[key]) {
          modifiedErrorSchema[key] = errorSchema[key]
        }
      })

      setErrorSchema(modifiedErrorSchema)
      setFormData(formData)
    }
  }

  const onAddToCartClick = async () => {
    // Without these the cart item has no child to book against: the drawer
    // groups it under "(undefined)" and the coin-calculation payload can't be
    // built, so the whole cart silently loses its pay buttons.
    if (!selectedStudentDetails?.user?.uuid || !selectedParentDetails?.uuid) {
      eventAlert(
        'Select a parent and child before adding this batch to the cart.',
        constants.ERROR
      )
      return
    }

    const userInfo = {
      student: selectedStudentDetails,
      parent: selectedParentDetails
    }

    const cartItem = getCartItemStructure(
      selectedItem,
      batchInfo[selectedItem.uuid],
      userInfo
    )

    const hasConflictingItems =
      !isCartEmpty && cartMetadata?.cartType !== 'excel'

    const addItemToCartAndClose = () => {
      if (isEditing) {
        updateCartItem(editingItem.id, cartItem)
        setValueInLocalStorage('CART_PARENT', selectedParentDetails)
        setCartMetadata({ cartType: selectedBatchType })
        onModalClose()
      }
      const cartValidation = validateItemForCart(currentCartItems, cartItem)
      if (cartValidation.isValid) {
        addItemToCart(cartItem)
        setValueInLocalStorage('CART_PARENT', selectedParentDetails)
        setCartMetadata({ cartType: selectedBatchType })
        onModalClose()
      } else {
        eventAlert(cartValidation?.message, constants.ERROR)
      }
    }

    if (hasConflictingItems) {
      confirm(
        () => {
          emptyCart()
          addItemToCartAndClose()
        },
        `Your cart contains items from a different batch type. Do you want to replace your existing cart with this item?`,
        () => {}, // Do nothing if user cancels
        {
          title: 'replace cart item?',
          icon: '',
          confirmButtonText: 'replace',
          icon: null,
          customClass: {
            confirmButton: 'cart-replace-confirm-button cart-replace-button',
            cancelButton: 'cart-replace-cancel-button cart-replace-button'
          },
          buttonsStyling: false
        }
      )
    } else {
      addItemToCartAndClose()
    }
  }

  const onModalClose = () => {
    setFormData({})
    setBatchInfo({})
    setErrorSchema({})
    // setSelectedParentDetails({})
    setSelectedStudentDetails({})
    setStudents([])
    setInputError({
      parent: null,
      student: null
    })
    setCommonCourses({})
    setStudentConflictDetails({})
    setEnableDiscovery(null)
    setCourseFormDisabled(true)
    setDisableAddtoCart(true)
    setCurrentBatchInfo({})
    onHandleModalClose()
  }

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      enableSave={false}
      showCloseIcon
      onModalClose={onModalClose}>
      <Styles.Container>
        <Styles.FormContainer>
          <ParentChildForm
            ref={formRef}
            bookingType={bookingType}
            schema={demoSchema}
            onSubmit={onSubmitForm}
            formData={formData}
            onChangeForm={handleOnFormDataChange}
            errorSchema={errorSchema}
            selectedParentDetails={selectedParentDetails}
            selectedStudentDetails={selectedStudentDetails}
            handleParentSelection={handleParentSelection}
            handleStudentSelection={handleStudentSelection}
            students={students}
            inputError={inputError}
            isParentLocked={isParentLocked}
            isEditing={isEditing}
            studentConflictDetails={studentConflictDetails}
            onHandleModalClose={onHandleModalClose}
          />
          <SeparationContainer $css={`flex:0.05;`}>
            <Divider orientation="vertical" />
          </SeparationContainer>
          <Styles.ItemsContainer>
            <CourseDetail
              allowStartDateDuringConflict={isClassConflictOnly}
              enableDiscovery={enableDiscovery}
              discoveryEndDate={discoveryEndDate}
              resubscribeStartDate={resubscribeStartDate}
              errorSchema={inputError}
              selectedItem={selectedItem}
              bookingType={bookingType}
              batchInfo={batchInfo}
              onBatchInfoChange={handleBatchInfoChange}
              selectedBatchType={selectedBatchType}
              coinData={coinData}
              courseFormDisabled={courseFormDisabled}
              isEditing={isEditing}
            />
          </Styles.ItemsContainer>
        </Styles.FormContainer>
      </Styles.Container>
      <Styles.ActionsContainer>
        <Actions
          onAddToCartClick={onAddToCartClick}
          disableAddToCart={disableAddToCart}
          isEditing={isEditing}
        />
      </Styles.ActionsContainer>
    </ModalForm>
  )
}

export default AddToCartModal
